<template>
  <Sidebar
    v-model:visible="visible"
    class="p-sidebar-lg modern-sidebar"
    position="right"
    :showCloseIcon="false"
  >
    <Loading v-if="saving" :title="'Saving configuration...'" :full="false" />
    <div v-show="!saving" class="config-form-container">
      <div class="flex align-items-center mb-4">
        <h3 class="text-xl font-semibold m-0">⚙️ Lead Configuration</h3>
      </div>
      
      <div class="space-y-6">
        <!-- Enable Lead Generation Popup -->
        <div class="config-item">
          <label class="config-label">Enable Lead Generation Popup</label>
          <div class="mt-2">
            <InputSwitch 
              v-model="config.leadGenerationPopupEnabled" 
              @change="updateConfig"
            />
            <span class="ml-2 text-sm text-gray-600">Show popup to users on key trading days</span>
          </div>
        </div>
        
        <!-- No Response Message -->
        <div class="config-item">
          <label class="config-label">No Response Message</label>
          <p class="text-sm text-gray-600 mb-2">Message shown when users click "No"</p>
          <div class="mb-2">
            <LanguageSelector v-model="selectedLanguage" />
          </div>
          <InputText
            :key="`leadGenerationPopupNoResponse-${selectedLanguage}`"
            id="leadGenerationPopupNoResponse"
            name="leadGenerationPopupNoResponse"
            class="w-full"
            placeholder="Type the message here"
            :modelValue="getConfigTranslation('leadGenerationPopupNoResponse')"
            @input="(event) => updateConfigTranslation('leadGenerationPopupNoResponse', event.target.value)"
          />
        </div>
        
        <!-- Save Button -->
        <div class="mt-6">
          <Button 
            label="Save Configuration" 
            @click="saveConfig" 
            :loading="saving"
            class="w-full save-config-button"
            icon="pi pi-save"
          />
        </div>
      </div>
    </div>
  </Sidebar>
</template>

<script>
import { ref, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { leadsApi } from '@/api/leads'
import LanguageSelector from '@/components/LanguageSelector.vue'
import InputSwitch from 'primevue/inputswitch'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Loading from '@/components/Loading.vue'
import Sidebar from 'primevue/sidebar'

export default {
  name: 'LeadConfigSidebar',
  components: {
    LanguageSelector,
    InputSwitch,
    InputText,
    Button,
    Loading,
    Sidebar
  },
  emits: ['update:isVisible', 'onSave'],
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const visible = ref(false)
    const toast = useToast()
    const selectedLanguage = ref('en')
    
    const config = ref({
      leadGenerationPopupEnabled: false,
      leadGenerationPopupNoResponse: {
        translations: [
          { languageCode: "en", text: "" },
          { languageCode: "da", text: "" },
          { languageCode: "fi", text: "" },
          { languageCode: "no", text: "" },
        ],
      },
    })

    const loadConfig = async () => {
      try {
        console.log('Loading lead configuration...')
        const response = await leadsApi.getLeadConfiguration()
        console.log('Lead configuration response:', response.data)
        
        if (response.data) {
          console.log('leadGenerationPopupNoResponse from API:', response.data.leadGenerationPopupNoResponse)
          console.log('Translations from API:', response.data.leadGenerationPopupNoResponse?.translations)
          
          config.value = {
            leadGenerationPopupEnabled: response.data.leadGenerationPopupEnabled || false,
            leadGenerationPopupNoResponse: response.data.leadGenerationPopupNoResponse || {
              translations: [
                { languageCode: "en", text: "" },
                { languageCode: "da", text: "" },
                { languageCode: "fi", text: "" },
                { languageCode: "no", text: "" },
              ],
            },
          }
          console.log('Loaded lead config:', config.value)
          console.log('Translations in config:', config.value.leadGenerationPopupNoResponse?.translations)
        } else {
          console.log('No lead configuration data received')
        }
      } catch (error) {
        console.error('Error loading lead configuration:', error)
        console.error('Error details:', error.response?.data)
      }
    }

    const getConfigTranslation = (field) => {
      console.log('getConfigTranslation called for field:', field)
      console.log('config.value:', config.value)
      console.log('config.value[field]:', config.value[field])
      console.log('selectedLanguage.value:', selectedLanguage.value)
      
      if (!config.value[field] || !config.value[field].translations) {
        console.log('Field or translations not found for:', field)
        return ""
      }
      
      const translation = config.value[field].translations.find(
        (t) => t.languageCode === selectedLanguage.value
      )
      console.log('Found translation:', translation)
      return translation ? translation.text : ""
    }

    const updateConfigTranslation = (field, value) => {
      console.log('updateConfigTranslation called:', field, value, 'for language:', selectedLanguage.value)
      
      if (!config.value[field] || !config.value[field].translations) {
        console.log('Field or translations not found:', field)
        return
      }
      
      let translation = config.value[field].translations.find(
        (t) => t.languageCode === selectedLanguage.value
      )
      
      if (!translation) {
        // Create new translation if it doesn't exist
        translation = {
          languageCode: selectedLanguage.value,
          text: value
        }
        config.value[field].translations.push(translation)
        console.log('Created new translation for language:', selectedLanguage.value)
      } else {
        translation.text = value
        console.log('Updated existing translation for language:', selectedLanguage.value)
      }
      
      console.log('Updated config:', config.value)
    }

    const updateConfig = () => {
      // This will be called when the switch is toggled
    }

    const saveConfig = async () => {
      try {
        console.log('Saving lead config:', config.value)
        
        // Convert to plain object to avoid Proxy issues
        const configData = {
          leadGenerationPopupEnabled: config.value.leadGenerationPopupEnabled,
          leadGenerationPopupNoResponse: {
            translations: JSON.parse(JSON.stringify(config.value.leadGenerationPopupNoResponse.translations))
          }
        }
        console.log('Sending to API:', configData)
        
        await leadsApi.updateLeadConfiguration(configData)
        
        // Show success message
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Lead configuration saved successfully',
          life: 3000,
        })
        
        emit('onSave', configData)
      } catch (error) {
        console.error('Error saving lead configuration:', error)
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not save lead configuration: ' + error,
          life: 3000,
        })
      }
    }

    // Watch for language changes to debug localization
    watch(selectedLanguage, (newLang) => {
      console.log('Language changed to:', newLang)
      console.log('Current translation:', getConfigTranslation('leadGenerationPopupNoResponse'))
    })

    // Watch for visibility changes to load config when sidebar opens
    watch(() => props.isVisible, (newValue) => {
      visible.value = newValue
      if (newValue) {
        loadConfig()
      }
    })

    // Watch for local visible changes to emit to parent
    watch(visible, (newValue) => {
      emit('update:isVisible', newValue)
    })

    return {
      visible,
      config,
      selectedLanguage,
      getConfigTranslation,
      updateConfigTranslation,
      updateConfig,
      saveConfig
    }
  }
}
</script>

<style scoped>
.modern-sidebar {
  --sidebar-width: 500px;
}

.modern-sidebar :deep(.p-sidebar) {
  border-radius: 16px 0 0 16px;
  box-shadow: -5px 0 30px rgba(0, 0, 0, 0.1);
}

.config-form-container {
  padding: 0.5rem;
}

.config-item {
  margin-bottom: 1.5rem;
}

.config-label {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  display: block;
  margin-bottom: 0.5rem;
}

.save-config-button {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 0.5rem !important;
  padding: 0.75rem 1rem !important;
  font-weight: 600 !important;
  transition: all 0.2s ease !important;
}

.save-config-button:hover {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3) !important;
}

/* Form styling */
:deep(.p-inputtext) {
  border-radius: 8px;
  transition: all 0.2s ease;
}

:deep(.p-inputtext:hover) {
  border-color: var(--primary-color);
}

:deep(.p-inputswitch) {
  margin-right: 0.5rem;
}

/* Language selector styling */
:deep(.language-selector) {
  margin-bottom: 1rem;
}
</style> 